import React, { useState } from 'react';
import { useAgencyUsers } from '../../hooks/useAgencyUsers';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { Trash2, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ScrollArea from '../ui/shared/ScrollArea';
import Table from '../ui/shared/table/Table';
import TableHeader from '../ui/shared/table/TableHeader';
import TableBody from '../ui/shared/table/TableBody';
import TableRow from '../ui/shared/table/TableRow';
import TableCell from '../ui/shared/table/TableCell';
import { format } from 'date-fns';
import { formatDateInAccountTz, formatDateTimeInAccountTz, formatTimeInAccountTz } from '@/lib/accountTimezone'

export function AgencyUsers() {
  const { t } = useTranslation(['agency', 'common']);
  const { users, loading, error, addUser, removeUser } = useAgencyUsers();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addSuccess, setAddSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setAddError(t('users.invalidEmail'));
      return;
    }

    if (!name || name.trim().length === 0) {
      setAddError(t('users.nameRequired'));
      return;
    }

    setAdding(true);
    setAddError(null);
    setAddSuccess(null);

    try {
      const result = await addUser(name.trim(), email.trim());
      setAddSuccess(result.message);
      setName('');
      setEmail('');
      setTimeout(() => {
        setShowAddDialog(false);
        setAddSuccess(null);
      }, 2000);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : t('users.addError'));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (userId: string, userName: string) => {
    if (!confirm(t('users.confirmRemove', { name: userName }))) {
      return;
    }

    try {
      await removeUser(userId);
    } catch (err) {
      alert(err instanceof Error ? err.message : t('users.removeError'));
    }
  };

  const handleDialogChange = (open: boolean) => {
    setShowAddDialog(open);
    if (!open) {
      setName('');
      setEmail('');
      setAddError(null);
      setAddSuccess(null);
    }
  };

  const filteredUsers = users.filter((user) => {
    const search = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-[#E5E7EB] border-t-[#2563EB] rounded-full animate-spin dark:border-[#3F3F46] dark:border-t-[#60A5FA]"></div>
          <p className="text-sm text-muted-foreground">{t('users.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">{t('users.loadError')}</h3>
          <p className="text-muted-foreground text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="w-full mt-3 sm:mt-4 flex-wrap gap-3 flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('users.search')}
            className="w-[170px] sm:w-[220px] md:w-[270px] xl:w-[417px] h-11 rounded-xl text-sm placeholder:text-[#94A3B8] text-[#0F172A] border border-[#E5E7EB] bg-white px-3 focus-visible:border-[#2563EB] dark:placeholder:text-[#71717A] dark:text-[#FAFAFA] dark:border-[#3F3F46] dark:bg-[#27272A]"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Dialog open={showAddDialog} onOpenChange={handleDialogChange}>
            <DialogTrigger asChild>
              <button className="rounded-xl bg-[#2563EB] min-h-[40px] text-sm text-white font-medium px-4 flex justify-center items-center gap-2 shadow-sm transition-colors duration-200 hover:bg-[#1D4ED8]">
                <Plus className="w-4 h-4" />
                {t('users.add')}
              </button>
            </DialogTrigger>
            <DialogContent className="flex w-full max-w-md p-0 rounded-2xl border border-[#E5E7EB] bg-white text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] flex-col">
              <ScrollArea classNames={{ root: "max-h-[90dvh] p-5 overflow-auto" }}>
                <h3 className="text-xl font-semibold text-foreground mb-6">
                  {t('users.dialogTitle')}
                </h3>

                <form onSubmit={handleAddUser} className="w-full gap-4 flex flex-col">
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name" className="text-sm font-medium text-foreground">
                      {t('users.fullName')}
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setAddError(null);
                      }}
                      placeholder={t('users.fullNamePlaceholder')}
                      className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                      disabled={adding}
                      required
                    />
                  </div>

                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="email" className="text-sm font-medium text-foreground">
                      {t('users.email')}
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setAddError(null);
                      }}
                      placeholder={t('users.emailPlaceholder')}
                      className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                      disabled={adding}
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('users.addNotice')}
                    </p>
                  </div>

                  {addError && (
                    <div className="bg-[#FEF2F2] border border-red-200 text-[#DC2626] dark:bg-red-500/20 dark:border-red-500/50 dark:text-[#F87171] px-3 py-2 rounded text-sm">
                      {addError}
                    </div>
                  )}

                  {addSuccess && (
                    <div className="bg-[#ECFDF5] border border-green-200 text-[#16A34A] dark:bg-green-500/20 dark:border-green-500/50 dark:text-[#22C55E] px-3 py-2 rounded text-sm">
                      {addSuccess}
                    </div>
                  )}

                  <div className="flex gap-3 justify-end pt-2">
                    <button
                      type="button"
                      onClick={() => handleDialogChange(false)}
                      disabled={adding}
                      className="rounded-xl border border-[#E5E7EB] bg-white min-h-[40px] text-sm text-[#334155] font-medium px-4 transition-colors duration-200 hover:bg-[#F1F5F9] disabled:opacity-50 dark:border-[#3F3F46] dark:bg-transparent dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]"
                    >
                      {t('common:actions.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={adding || !email || !name}
                      className="rounded-xl bg-[#2563EB] min-h-[40px] text-sm text-white font-medium px-4 transition-colors duration-200 hover:bg-[#1D4ED8] disabled:opacity-50"
                    >
                      {adding ? t('users.adding') : t('users.confirmAdd')}
                    </button>
                  </div>
                </form>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {filteredUsers.length === 0 ? (
        <div className="flex-1 min-h-0">
          <div
            className="w-full h-full flex items-center justify-center border border-border rounded-[20px] bg-card shadow-sm"
            style={{ height: "calc(100vh - 300px)" }}
          >
            <div className="flex flex-col items-center gap-4 text-center p-6">
              <div className="w-16 h-16 bg-card-muted rounded-full flex items-center justify-center">
                <Plus className="w-8 h-8 text-muted-foreground" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {searchTerm ? t('users.emptySearch') : t('users.empty')}
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {searchTerm
                    ? t('users.emptySearchHint')
                    : t('users.emptyHint')}
                </p>
                {!searchTerm && (
                  <button
                    onClick={() => setShowAddDialog(true)}
                    className="rounded-xl bg-[#2563EB] min-h-[40px] text-sm text-white font-medium px-4 flex justify-center items-center gap-2 shadow-sm transition-colors duration-200 hover:bg-[#1D4ED8] mx-auto"
                  >
                    <Plus className="w-4 h-4" />
                    {t('users.addFirst')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0">
          <div
            className="w-full max-h-full overflow-auto border border-border rounded-[20px] bg-card shadow-sm table-scrollbar"
            style={{ height: "calc(100vh - 300px)" }}
          >
            <Table className="min-w-[800px]">
              <TableHeader className="border-b border-border sticky top-0 bg-card z-10">
                <TableRow>
                  {(['colUser', 'colEmail', 'colCreatedAt', 'colActions'] as const).map((key) => (
                    <TableCell
                      key={key}
                      as="th"
                      className={`!text-[#475569] !font-semibold !text-[13px] !py-4 ${key === 'colActions' ? "!text-right" : ""}`}
                    >
                      <div className={`flex items-center gap-1 ${key === 'colActions' ? "justify-end" : "justify-start"}`}>
                        <p>{t(`users.${key}`)}</p>
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow
                    key={user.id}
                    className="hover:bg-[#F8FAFC] dark:hover:bg-hover transition-colors duration-200 border-b border-border/60 last:border-b-0"
                  >
                    <TableCell as="td" className="!py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#EEF4FF] dark:bg-blue-500/20 flex items-center justify-center">
                          <span className="text-[#2563EB] dark:text-[#60A5FA] font-semibold text-sm">
                            {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <p className="font-semibold text-foreground">
                          {user.name || t('users.fallbackName')}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell as="td" className="!py-4 !text-[#334155] dark:!text-foreground">
                      {user.email || 'N/A'}
                    </TableCell>
                    <TableCell as="td" className="!py-4 !text-[#334155] dark:!text-foreground">
                      {formatDateInAccountTz(user.created_at)}
                    </TableCell>
                    <TableCell as="td" className="!py-4">
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleRemove(user.id, user.name || user.email || t('users.thisUser'))}
                          className="text-red-400 hover:text-red-300 p-2 rounded hover:bg-red-500/10 transition-colors"
                          title={t('users.remove')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
